import { Box, Text, tinyButtons, useContextPopoverHeight, usePopoverContext } from '@byecode/ui'
import { mergeRefs } from '@byecode/ui/hooks/useMergedRef'
import { type Field, type PageVariableSchema, type TypeInstanceMap, type VariableADTvalue, VariableType } from '@lighthouse/core'
import { filterObjectUndefined } from '@lighthouse/tools'
import React, { Fragment, useMemo, useState } from 'react'
import styled from 'styled-components'

import type { FetchPexelsParams, PexelsResponse } from '../../../types/file'
import type { UploadDropZoneProps } from '../../UploadManage'
import type { VariableOption, VariableTree2 } from '../types'
import { VariableCustom } from './Custom'
import { VariableItem } from './Item'
import VariableSelectDataSource from './SelectDataSource'

const formulaOption: VariableTree2 = {
    value: 'formula',
    type: VariableType.FORMULA,
    label: '公式',
    icon: 'PropertyFormula'
}

export interface VariableDropDownOption {
    /**
     * 变量过滤 innerTypes（仅用于过滤 options，不影响 fields 区域逻辑）
     * - undefined / []：不做过滤
     * - 非空数组：仅允许 `fieldInnerType` 在 innerTypes 中的变量
     */
    innerTypes?: TypeInstanceMap[]
    /** 多个数据表选择 */
    datasourceOptions?: VariableTree2[]

    /** 输入框组件 */
    inputOption?: VariableTree2
    /** 筛选控制器组件 */
    filterOption?: VariableTree2
    /** 视图组件 */
    viewOptions?: VariableTree2[]
    /** 表单组件 */
    formOption?: VariableTree2

    /** 页面数据 */
    pageOptions?: VariableTree2[]

    /** 事件数据 */
    eventOption?: VariableTree2

    /** 循环内部数据 */
    loopOption?: VariableTree2

    /** 上游节点数据 */
    upstreamOptions?: VariableTree2[]

    /** 当前数据表数据 */
    datasourceOption?: VariableTree2

    /** 用户数据 */
    userOption?: VariableTree2
    /** 系统日期数据 */
    dateOption?: VariableTree2
    /** 图片数据 */
    imageOption?: VariableTree2

    disabledFormula?: boolean
}

export interface VariableSelectDropDownProps extends VariableDropDownOption, Omit<React.ComponentPropsWithoutRef<'div'>, 'onChange'> {
    /** Field选项 */
    fields?: Field[]
    /** 可多选值 */
    multiple?: boolean
    /** 上传文件时参数 */
    uploadProps?: Pick<UploadDropZoneProps, 'uploadOptions' | 'accept' | 'multiple'>
    /** 禁用Pexels */
    disablePexels?: boolean
    // 公式
    enableFormula?: boolean
    /** 查询数据源参数 */
    selectDataSourceProps?: {
        /** 输入框组件 */
        inputOption?: VariableTree2
        /** 视图组件 */
        viewOptions?: VariableTree2[]
        /** 页面数据 */
        pageOptions?: VariableTree2[]
        /** 上游节点数据 */
        upstreamOptions?: VariableTree2[]
        /** 用户数据 */
        userOption?: VariableTree2

        /** 验证字段是否可使用 */
        validateFieldType?: (field: Field) => boolean
    }

    pageVariableSchemas?: PageVariableSchema[]

    empty?: React.ReactNode

    value?: VariableADTvalue
    onChange: (value: VariableADTvalue) => void
    /** 上传进度回调 */
    onUploadProgressChange?: (percent: number) => void
    /** 打开选择数据源数据 弹窗 */
    onOpenSelectDataSourceModal?: () => void

    onFetchPexelsList?: (params: FetchPexelsParams) => Promise<PexelsResponse>
}

const SCxDropDownContainer = styled.div<{ maxHeight?: number }>`
    width: 100%;
    max-height: ${({ maxHeight }) => `${maxHeight}px`};
    overflow: hidden auto;
    ${tinyButtons}

    &>.variable-select-drop-down-item:not(.byecode-popover-dropdown):not(:first-child) {
        &::before {
            display: block;
            content: '';
            width: 100%;
            height: 0.5px;
            background-color: var(--color-gray-200);
            margin: 8px 0; // 8px 会影响横线粗细  灿白说可以改为9px
        }
    }
`

const buildMatchInnerTypes = (innerTypes: TypeInstanceMap[] | undefined) => {
    const defaultExcluded = new Set<TypeInstanceMap>(['NULL'])

    // 不传 / 空数组：默认不过滤，但禁用 NULL / RICH_TEXT
    if (!innerTypes || innerTypes.length === 0) {
        return (arg: Field | TypeInstanceMap) => {
            const innerType = typeof arg === 'object' ? arg.innerType : arg
            return !defaultExcluded.has(innerType)
        }
    }

    // 传了 innerTypes：白名单匹配
    return (arg: Field | TypeInstanceMap) => {
        const innerType = typeof arg === 'object' ? arg.innerType : arg
        return innerTypes.includes(innerType) && !defaultExcluded.has(innerType)
    }
}

const filterVariableTreeByInnerTypes = (
    tree: VariableTree2[] | undefined,
    match: ((arg: Field | TypeInstanceMap) => boolean) | undefined
): VariableTree2[] | undefined => {
    if (!match || !tree || tree.length === 0) {
        return tree
    }

    return tree
        .map(node => {
            const children = node.children ? filterVariableTreeByInnerTypes(node.children, match) : node.children
            return children !== node.children ? { ...node, children } : node
        })
        .filter(node => {
            // 叶子节点：按 fieldInnerType 过滤；没有 fieldInnerType 的叶子节点默认保留
            if (Object.hasOwn(node, 'fieldInnerType') && node.fieldInnerType) {
                return match(node.fieldInnerType)
            }
            return true
        })
}

const filterVariableOptionByInnerTypes = (
    option: VariableTree2 | undefined,
    match: ((arg: Field | TypeInstanceMap) => boolean) | undefined
): VariableTree2 | undefined => {
    if (!match || !option) {
        return option
    }
    const children = option.children ? filterVariableTreeByInnerTypes(option.children, match) : option.children
    const next = children !== option.children ? { ...option, children } : option

    if (Object.hasOwn(next, 'fieldInnerType') && next.fieldInnerType) {
        return match(next.fieldInnerType) ? next : undefined
    }
    if (next.children) {
        // 保留 children 为空数组的节点，让 UI 可以展示“空的二级菜单”
        return next
    }
    return next
}

export const VariableSelectDropDown = React.forwardRef<HTMLDivElement, VariableSelectDropDownProps>(
    (
        {
            innerTypes,
            datasourceOptions,
            inputOption,
            viewOptions,
            formOption,
            filterOption,
            pageOptions,
            eventOption,
            loopOption,
            upstreamOptions,
            datasourceOption,

            userOption,
            dateOption,
            imageOption,

            fields,
            multiple,
            uploadProps,
            disablePexels,
            enableFormula,
            selectDataSourceProps,

            empty,

            value,
            onChange,
            onUploadProgressChange,
            onOpenSelectDataSourceModal,
            onFetchPexelsList
        },
        ref
    ) => {
        const realFields = fields?.filter(field => field.type)
        const { context } = usePopoverContext()
        const [maxHeight, internalRef] = useContextPopoverHeight({ context })
        const [active, setActive] = useState<string>('')

        const matchInnerTypes = useMemo(() => buildMatchInnerTypes(innerTypes), [innerTypes])

        // 仅过滤 options，不影响 fields 的展示/逻辑
        const filteredDataSourceOptions = useMemo(
            () => filterVariableTreeByInnerTypes(datasourceOptions, matchInnerTypes),
            [datasourceOptions, matchInnerTypes]
        )
        const filteredInputOption = useMemo(
            () => filterVariableOptionByInnerTypes(inputOption, matchInnerTypes),
            [inputOption, matchInnerTypes]
        )
        const filteredFilterOption = useMemo(
            () => filterVariableOptionByInnerTypes(filterOption, matchInnerTypes),
            [filterOption, matchInnerTypes]
        )
        const filteredFormOption = useMemo(
            () => filterVariableOptionByInnerTypes(formOption, matchInnerTypes),
            [formOption, matchInnerTypes]
        )
        const filteredViewOptions = useMemo(
            () => filterVariableTreeByInnerTypes(viewOptions, matchInnerTypes),
            [viewOptions, matchInnerTypes]
        )
        const filteredPageOptions = useMemo(
            () => filterVariableTreeByInnerTypes(pageOptions, matchInnerTypes),
            [pageOptions, matchInnerTypes]
        )
        const filteredEventOption = useMemo(
            () => filterVariableOptionByInnerTypes(eventOption, matchInnerTypes),
            [eventOption, matchInnerTypes]
        )
        const filteredLoopOption = useMemo(
            () => filterVariableOptionByInnerTypes(loopOption, matchInnerTypes),
            [loopOption, matchInnerTypes]
        )
        const filteredUpstreamOptions = useMemo(
            () => filterVariableTreeByInnerTypes(upstreamOptions, matchInnerTypes),
            [upstreamOptions, matchInnerTypes]
        )
        const filteredDatasourceOption = useMemo(
            () => filterVariableOptionByInnerTypes(datasourceOption, matchInnerTypes),
            [datasourceOption, matchInnerTypes]
        )
        const filteredUserOption = useMemo(
            () => filterVariableOptionByInnerTypes(userOption, matchInnerTypes),
            [userOption, matchInnerTypes]
        )
        const filteredDateOption = useMemo(
            () => filterVariableOptionByInnerTypes(dateOption, matchInnerTypes),
            [dateOption, matchInnerTypes]
        )

        const filteredImageOption = useMemo(
            () => filterVariableOptionByInnerTypes(imageOption, matchInnerTypes),
            [imageOption, matchInnerTypes]
        )

        const options = useMemo(() => {
            const data: VariableOption[] = []

            if (filteredDataSourceOptions && filteredDataSourceOptions.length > 0) {
                data.push({ title: '数据表', list: filteredDataSourceOptions })
            }

            if (filteredDatasourceOption) {
                data.push({ title: '当前数据表', list: [filteredDatasourceOption] })
            }

            if (filteredInputOption || filteredViewOptions || filteredFormOption || filteredFilterOption || filteredImageOption) {
                const componentOptions = [filteredInputOption, filteredFilterOption, filteredFormOption, filteredImageOption].filter(
                    filterObjectUndefined
                )
                if (filteredViewOptions && filteredViewOptions.length > 0) {
                    componentOptions.push(...filteredViewOptions)
                }
                if (componentOptions.length > 0) {
                    data.push({
                        title: '组件数据',
                        list: componentOptions
                    })
                }
            }

            if (filteredPageOptions && filteredPageOptions.length > 0) {
                data.push({ title: '页面数据', list: filteredPageOptions })
            }

            if (filteredEventOption) {
                data.push({ title: '事件', list: [filteredEventOption] })
            }

            if (filteredLoopOption) {
                data.push({ title: '循环', list: [filteredLoopOption] })
            }

            if (filteredUpstreamOptions && filteredUpstreamOptions.length > 0) {
                data.push({ title: '上游节点输出的数据', list: filteredUpstreamOptions })
            }

            if (filteredUserOption || filteredDateOption) {
                data.push({ title: '全局数据', list: [filteredUserOption, filteredDateOption].filter(filterObjectUndefined) })
            }

            return data
        }, [
            filteredDataSourceOptions,
            filteredDatasourceOption,
            filteredDateOption,
            filteredEventOption,
            filteredFilterOption,
            filteredFormOption,
            filteredImageOption,
            filteredInputOption,
            filteredLoopOption,
            filteredPageOptions,
            filteredUpstreamOptions,
            filteredUserOption,
            filteredViewOptions
        ])

        const isEmpty = options.length === 0 && (!realFields || realFields.length === 0) && !enableFormula

        return (
            <SCxDropDownContainer maxHeight={maxHeight} ref={mergeRefs(ref, internalRef)}>
                {isEmpty ? (
                    empty
                ) : (
                    <>
                        {((realFields && realFields.length > 0) || selectDataSourceProps) && (
                            <>
                                {realFields?.map((field, index) => {
                                    const key = field.id || String(index)
                                    return (
                                        <VariableCustom
                                            key={field.id}
                                            field={field}
                                            value={value?.type === VariableType.VALUE ? value : undefined}
                                            isMultiple={multiple}
                                            uploadProps={uploadProps}
                                            disabledPexels={disablePexels}
                                            opened={active === key}
                                            onOpenChange={v => {
                                                setActive(v ? key : '')
                                            }}
                                            onChange={onChange}
                                            onChangeFileProcess={onUploadProgressChange}
                                            onFetchPexelsList={onFetchPexelsList}
                                        />
                                    )
                                })}

                                {selectDataSourceProps && (
                                    <VariableSelectDataSource
                                        value={value?.type === VariableType.SELECT_DATASOURCE ? value : undefined}
                                        onOpen={onOpenSelectDataSourceModal}
                                    />
                                )}
                            </>
                        )}
                        {enableFormula && (
                            <Box>
                                <VariableItem value={value} data={formulaOption} onChange={onChange} path={[formulaOption.value]} />
                            </Box>
                        )}
                        {options.map(option => (
                            <Fragment key={option.title}>
                                <Box className="variable-select-drop-down-item">
                                    {option.title && (
                                        <Text size={12} color="var(--color-gray-500)" lineHeight="32px" px={16}>
                                            {option.title}
                                        </Text>
                                    )}
                                    {option.list.map(item => (
                                        <VariableItem key={item.value} value={value} data={item} onChange={onChange} path={[item.value]} />
                                    ))}
                                </Box>
                            </Fragment>
                        ))}
                    </>
                )}
            </SCxDropDownContainer>
        )
    }
)
